
import React, {useState, useEffect, useCallback} from 'react';
import { useAuth } from '../contexts/AuthContext';
import { athletesAPI } from '../api';
import { useTranslation } from '../i18n';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from '../components/common/Notifier';

export const ProfilePage = () => {
  const { t } = useTranslation();
  const { athlete, updateAthlete } = useAuth();
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    language: 'en',
    birth_date: '',
    email: '',
  });
    const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_new_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false)
  
  const [weightData, setWeightData] = useState({
    weight: '',
  });
  const [weightHistory, setWeightHistory] = useState([]);
  const [vdotHistory, setVdotHistory] = useState([]);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('avatar', avatarFile);

      const result = await updateAthlete(formData);
      if (result.success) {
        toast.success(t('profile.avatar_updated'));
        setAvatarFile(null);
        setAvatarPreview(null);
      } else {
        toast.error(result.error || t('error.general'));
      }
    } catch (error) {
      toast.error(t('error.general'));
    } finally {
      setUploadingAvatar(false);
    }
  };


  const loadWeightHistory = useCallback(async () => {
    if (!athlete) return;
    try {
      const response = await athletesAPI.get(athlete.id);
      setWeightHistory(response.data.body_metrics || []);
    } catch (error) {
      console.error('Error loading weight history:', error);
    }
  }, [athlete]);

  // useCallback для loadVdotHistory
  const loadVdotHistory = useCallback(async () => {
    if (!athlete) return;
    try {
      const response = await athletesAPI.vdotHistory(athlete.id);
      setVdotHistory(response.data || []);
    } catch (error) {
      console.error('Error loading VDOT history:', error);
    }
  }, [athlete]);

  // Добавляем зависимости
  useEffect(() => {
    if (athlete) {
      setFormData({
        name: athlete.name || '',
        language: athlete.language || 'ru',
        birth_date: athlete.birth_date || '',
        email: athlete.email || '',
      });
      loadWeightHistory();
      loadVdotHistory();
    }
  }, [athlete, loadWeightHistory, loadVdotHistory]);
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_new_password) {
      toast.error(t('profile.password_mismatch'));
      return;
    }
    setChangingPassword(true);
    try {
      await athletesAPI.changePassword(passwordData.current_password, passwordData.new_password);
      toast.success(t('profile.password_changed'));
      setPasswordData({ current_password: '', new_password: '', confirm_new_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || t('error.general'));
    } finally {
      setChangingPassword(false);
    }
  };
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await updateAthlete(formData);
    if (result.success) {
      toast.success(t('profile.update_success'));
      setEditing(false);
    } else {
      toast.error(result.error || t('error.general'));
    }
    setLoading(false);
  };


  const handleAddWeight = async (e) => {
    e.preventDefault();
    if (!weightData.weight) {
      toast.error(t('profile.weight_required'));
      return;
    }

    setLoading(true);
    try {
      await athletesAPI.addWeight(athlete.id, parseFloat(weightData.weight));
      toast.success(t('profile.weight_added'));
      setWeightData({ weight: '' });
      loadWeightHistory();
    } catch (error) {
      toast.error(t('error.general'));
    }
    setLoading(false);
  };

  if (!athlete) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-3xl font-bold text-gray-800">
        👤 {t('profile.title')}
      </h1>
      <div className="bg-white rounded-lg shadow p-6 flex items-center gap-6">
        <img
          src={avatarPreview || athlete.avatar_url || '/avatar-placeholder.png'}
          alt=""
          className="w-24 h-24 rounded-full object-cover border border-gray-200"
        />
        <div className="space-y-2">
          <label className="inline-block text-sm text-blue-600 hover:text-blue-800 cursor-pointer">
            📷 {t('profile.change_avatar')}
            <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
          </label>
          {avatarFile && (
            <button
              onClick={handleAvatarUpload}
              disabled={uploadingAvatar}
              className="block bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
            >
              {uploadingAvatar ? t('common.loading') : t('common.save')}
            </button>
          )}
        </div>
      </div>
      {/* Основная информация */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {t('profile.personal_info')}
          </h2>
          <button
            onClick={() => setEditing(!editing)}
            className="text-blue-600 hover:text-blue-800"
          >
            {editing ? t('common.cancel') : `✏️ ${t('profile.edit')}`}
          </button>
        </div>

        {editing ? (
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('profile.name')}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('profile.language')}
              </label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                className="mt-1 block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="ru">🇷🇺 Русский</option>
                <option value="uk">🇺🇦 Українська</option>
                <option value="en">🇬🇧 English</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('profile.email')}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('profile.birth_date')}
              </label>
              <input
                type="date"
                value={formData.birth_date}
                onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                className="mt-1 block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg disabled:opacity-50 transition-all"
            >
              {loading ? t('common.loading') : t('common.save')}
            </button>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">{t('profile.name')}</span>
              <span className="font-medium">{athlete.name}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">{t('profile.email')}</span>
              <span className="font-medium">{athlete.email || '—'}</span>
            </div>


            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">{t('profile.telegram_id')}</span>
              <span className="font-medium">{athlete.telegram_id || '—'}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">{t('profile.language')}</span>
              <span className="font-medium">
                {athlete.language === 'ru' ? '🇷🇺 Русский' :
                 athlete.language === 'uk' ? '🇺🇦 Українська' :
                 '🇬🇧 English'}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">{t('profile.birth_date')}</span>
              <span className="font-medium">
                {athlete.birth_date ? format(new Date(athlete.birth_date), 'dd.MM.yyyy', { locale: ru }) : '—'}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">{t('profile.registered')}</span>
              <span className="font-medium">
                {format(new Date(athlete.created_at), 'dd.MM.yyyy', { locale: ru })}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500">{t('profile.access_level')}</span>
                <span className="font-medium">
                  {athlete.access_level === 30 ? t('profile.access_level_admin') :
                   athlete.access_level === 20 ? t('profile.access_level_premium') :
                   t('profile.access_level_free')}
                </span>
            </div>
          </div>
        )}
      </div>
      <>{/* Смена пароля */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            🔒 {t('profile.password_title')}
          </h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('profile.current_password')}
              </label>
              <div className="relative mt-1">
                <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                    className="block w-full px-4 py-2.5 pr-12 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    autoComplete="current-password"
                    required
                />
                <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showCurrentPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('profile.new_password')}
              </label>
              <div className="relative mt-1">
                <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                    className="block w-full px-4 py-2.5 pr-12 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    autoComplete="new-password"
                    required
                />
                <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showNewPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('profile.confirm_new_password')}
              </label>
              <div className="relative mt-1">
                <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirm_new_password}
                    onChange={(e) => setPasswordData({...passwordData, confirm_new_password: e.target.value})}
                    className="block w-full px-4 py-2.5 pr-12 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    autoComplete="new-password"
                    required
                />
                <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <button
                type="submit"
                disabled={changingPassword}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg disabled:opacity-50 transition-all"
            >
              {changingPassword ? t('common.loading') : t('profile.change_password_button')}
            </button>
          </form>
        </div>
      </>
      {/* Вес */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          ⚖️ {t('profile.weight')}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Текущий вес */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">{t('profile.current_weight')}</div>
            <div className="text-3xl font-bold text-blue-600">
              {athlete.weight ? `${athlete.weight} ${t('common.kg')}` : '—'}
            </div>
          </div>

          {/* Добавить вес */}
          <form onSubmit={handleAddWeight} className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">
                {t('profile.add_weight')}
              </label>
              <input
                type="number"
                step="0.1"
                value={weightData.weight}
                onChange={(e) => setWeightData({ weight: e.target.value })}
                placeholder={t('profile.weight_placeholder')}
                className="mt-1 block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg disabled:opacity-50 whitespace-nowrap"
            >
              {t('profile.add_weight_btn')}
            </button>
          </form>
        </div>

        {/* История веса */}
        {weightHistory.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              {t('profile.weight_history')}
            </h3>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {weightHistory.slice(0, 10).map((record) => (
                <div key={record.id} className="flex justify-between text-sm py-1 border-b border-gray-100">
                  <span className="text-gray-600">
                    {format(new Date(record.created_at), 'dd.MM.yyyy HH:mm', { locale: ru })}
                  </span>
                  <span className="font-medium">{record.weight} {t('common.kg')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* VDOT */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          📈 {t('profile.vdot_history')}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">{t('profile.current_vdot')}</div>
            <div className="text-3xl font-bold text-purple-600">
              {athlete.current_vdot || '—'}
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">{t('profile.max_vdot')}</div>
            <div className="text-3xl font-bold text-green-600">
              {vdotHistory.length > 0 ? Math.max(...vdotHistory.map(v => v.vdot)) : '—'}
            </div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">{t('profile.total_records')}</div>
            <div className="text-3xl font-bold text-orange-600">
              {vdotHistory.length}
            </div>
          </div>
        </div>

        {/* История VDOT */}
        {vdotHistory.length > 0 && (
          <div className="max-h-40 overflow-y-auto space-y-1">
            {vdotHistory.slice().reverse().slice(0, 10).map((record, index) => (
              <div key={index} className="flex justify-between text-sm py-1 border-b border-gray-100">
                <span className="text-gray-600">
                  {format(new Date(record.date), 'dd.MM.yyyy', { locale: ru })}
                  <span className="ml-2 text-xs text-gray-400">
                    {record.source === 'race' ? '🏁' :
                     record.source === 'cooper' ? '🏃' :
                     record.source === 'estimate' ? '📊' : '✏️'}
                  </span>
                </span>
                <span className="font-medium">VDOT: {record.vdot}</span>
              </div>
            ))}
          </div>
        )}

        {vdotHistory.length === 0 && (
          <div className="text-gray-500 text-center py-4">
            {t('profile.no_vdot_records')}
          </div>
        )}
      </div>

      {/* Статистика */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          📊 {t('profile.stats_title')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {athlete.workouts?.length || 0}
            </div>
            <div className="text-sm text-gray-600">{t('profile.total_workouts')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {athlete.workouts?.filter(w => w.is_completed).length || 0}
            </div>
            <div className="text-sm text-gray-600">{t('profile.completed')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {athlete.goal_races?.filter(g => g.status === 'planned').length || 0}
            </div>
            <div className="text-sm text-gray-600">{t('profile.active_goals')}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {athlete.race_results?.length || 0}
            </div>
            <div className="text-sm text-gray-600">{t('profile.results')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};